"use client";
import {JSX, useEffect} from "react";
import Button from "@@/components/ui/Button";
import Image, {StaticImageData} from "next/image";
import vIcon from "@@/public/img/shared/v.svg";
import useAppearOnScroll from "@@/hooks/useAppearsOnScroll";
import styles from "@@/styles/components/shared/CheckedList.module.scss"
import {exo2Bold} from "@@/utils/fonts";

interface Props {
    title: string;
    cta: string;
    ctaUrl?: string;
    icon: string;
    className?: string;
    items: {text: string}[];
    infoBubble?: {
        title: string;
        text: string;
    }
    ctaStyle?: {
        bgColor: string;
        textColor: string;
        className: string;
    }
    iconsBgColor?: string;
    infoBubbleColor?: string;
    id?: string;
}

export default function CheckedList({ title, cta, ctaUrl, icon, className, items, infoBubble, ctaStyle, iconsBgColor, infoBubbleColor, id }: Props): JSX.Element {

    const titleAnim = useAppearOnScroll<HTMLHeadingElement>({ from: "fromBottom", duration: 800, delay: 0 });
    const btnAnim = useAppearOnScroll<HTMLHeadingElement>({ from: "fromRight", duration: 800, delay: 200});
    const infoBubbleAnim = useAppearOnScroll<HTMLHeadingElement>({ from: "fromRight", duration: 800, delay: 200});

    const animatedItems = Array.isArray(items)
        ? items.map((item, index) => {
            const animation = useAppearOnScroll<HTMLLIElement>({
                from: "fromBottom",
                duration: 500,
                delay: index * 100,
                offset: 200
            });
            return { ...item, animation };
        }) : [];

    useEffect(() => {}, [animatedItems.length]);

    return (
        <div className={`position-relative py-5 py-1-mobile h-fit`} id={id}>
            <div className={`d-flex flex-column gap-5 p-5 ${className} rounded-1`}>
                <div className={`d-flex gap-2 flex-wrap align-items-center`}>
                    <div ref={titleAnim.ref} className={`flex-1 min-w-50 d-flex gap-1 align-items-center ${titleAnim.className}`} style={titleAnim.style}>
                        <Image src={icon} alt={`dev icon`} className={`cardIcon`} style={{background: iconsBgColor || 'var(--blue-300)'}} />
                        <h2 className={`${exo2Bold.className} ${styles.title}`}
                            dangerouslySetInnerHTML={{__html: title}}
                        ></h2>
                    </div>
                    <div ref={btnAnim.ref} className={`${btnAnim.className} min-w-100-mobile`} style={btnAnim.style}>
                        <Button href={ctaUrl || ''} title={cta} bgColor={ctaStyle?.bgColor || `var(--blue-300)`} className={`${ctaStyle?.className} min-w-100-mobile`} textColor={ctaStyle?.textColor || 'var(--blue-800)'}></Button>
                    </div>
                </div>
                <div className={`d-flex gap-2 h-fit flex-column-mobile`}>
                    <ul className={`flex-1 d-flex flex-wrap list-none gap-2 h-fit`}>
                        {animatedItems.map((item, index) => (
                            <li key={index}
                                ref={item.animation.ref}
                                className={`d-flex gap-1 flex-1 min-w-50 h-fit mx-width-50percent ${item.animation.className}`}
                                style={item.animation.style}>
                                {icon && (
                                    <Image src={vIcon} alt={`v icon`} className={`cardIcon tiny rounded-5`} style={{background: iconsBgColor || 'var(--blue-300)'}} aria-hidden={true}/>
                                )}
                                <p>{item.text}</p>
                            </li>
                        ))}
                    </ul>
                    {infoBubble && (
                        <div ref={infoBubbleAnim.ref}
                             className={`rounded-1 p-2 min-h-fit min-w-200 w-100 flex-03 d-flex flex-column gap-1 ${infoBubbleAnim.className}`}
                             style={{...infoBubbleAnim.style, background: infoBubbleColor || 'var(--grey-000)'}}>
                            <h3>{infoBubble.title}</h3>
                            <p>{infoBubble.text}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}